import React, { useEffect, useState } from "react";
import API_BASE from "../config/api";
import SetupGuideModal from "./SetupGuideModal";
import PoliticalProfileTab from "./PoliticalProfileTab";
import PoliticalAchievementsTab from "./PoliticalAchievementsTab";
import PoliticalGalleryTab from "./PoliticalGalleryTab";

/*
  U13 — Dashboard page to manage the customer's Political Website.
  Tab-based interface (Profile / Achievements / Gallery) like BusinessWebsiteManager.
  All data flows through a single shared "Save Website" action.
  props: userData, listinguid, onBack
*/

const emptyAchievement = { title: "", year: "", description: "" };
const emptyTimelineEntry = { year: "", title: "", description: "" };

export default function PoliticalWebsiteManager({ userData, listinguid, onBack }) {
    const localUser = JSON.parse(localStorage.getItem("user") || "{}");
    const personaluid = userData?.personaluid || localUser?.personaluid;

    const [tab, setTab] = useState("profile");

    const [form, setForm] = useState({
        name: "",
        position_title: "",
        party: "",
        tagline: "",
        photo: "",
        cover: "",
        phone: "",
        email: "",
        address: "",
        website: "",
        about: "",
        linkfacebook: "",
        linkinstagram: "",
        linktwitter: "",
        linklinkedin: "",
        youtube_link: "",
        theme_color: "classic-navy",
    });
    const [achievements, setAchievements] = useState([{ ...emptyAchievement }]);
    const [timeline, setTimeline] = useState([{ ...emptyTimelineEntry }]);
    const [gallery, setGallery] = useState([""]);
    const [domain, setDomain] = useState("");
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState(null);

    // Load data on mount
    useEffect(() => {
        if (!personaluid) return;
        fetch(`${API_BASE}/api/get_political_site.php?personaluid=${encodeURIComponent(personaluid)}`)
            .then((r) => r.json())
            .then((res) => {
                if (res.domain) setDomain(res.domain);
                if (res.success && res.data) {
                    const d = res.data;
                    setForm({
                        name: d.name || "",
                        position_title: d.position_title || "",
                        party: d.party || "",
                        tagline: d.tagline || "",
                        photo: d.photo || "",
                        cover: d.cover || "",
                        phone: d.phone || "",
                        email: d.email || "",
                        address: d.address || "",
                        website: d.website || "",
                        about: d.about || "",
                        linkfacebook: d.linkfacebook || "",
                        linkinstagram: d.linkinstagram || "",
                        linktwitter: d.linktwitter || "",
                        linklinkedin: d.linklinkedin || "",
                        youtube_link: d.youtube_link || "",
                        theme_color: d.theme_color || "classic-navy",
                    });
                    setAchievements(d.achievements?.length ? d.achievements : [{ ...emptyAchievement }]);
                    setTimeline(d.timeline?.length ? d.timeline : [{ ...emptyTimelineEntry }]);
                    setGallery(d.gallery?.length ? d.gallery : [""]);
                }
            })
            .catch(() => { });
    }, [personaluid]);

    const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

    const handleSave = async () => {
        if (!form.name.trim()) {
            setMsg({ type: "warning", text: "Full name is required." });
            setTab("profile");
            return;
        }

        const yt = (form.youtube_link || "").trim();
        if (yt && !/^https?:\/\/(www\.)?(youtube\.com|youtu\.be)\//i.test(yt)) {
            setMsg({ type: "warning", text: "YouTube field must be a valid YouTube link." });
            setTab("gallery");
            return;
        }

        setSaving(true);
        setMsg(null);
        try {
            const res = await fetch(`${API_BASE}/api/save_political_site.php`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    personaluid,
                    listinguid,
                    ...form,
                    youtube_link: yt,
                    achievements: achievements.filter((a) => (a.title || "").trim() !== ""),
                    timeline: timeline.filter((t) => (t.title || "").trim() !== ""),
                    gallery: gallery.filter((g) => (g || "").trim() !== ""),
                }),
            });
            const data = await res.json();
            setSaving(false);
            if (data.success) {
                setDomain(data.domain);
                setMsg({ type: "success", text: "Political website saved successfully." });
            } else {
                setMsg({ type: "danger", text: data.message || "Could not save." });
            }
        } catch {
            setSaving(false);
            setMsg({ type: "danger", text: "Network error while saving." });
        }
    };

    const tabs = [
        ["profile", "Profile"],
        ["achievements", "Achievements & Timeline"],
        ["gallery", "Gallery & Settings"],
    ];

    return (
        <div className="container-fluid py-2" style={{ maxWidth: 980 }}>
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
                    onClick={() => onBack && onBack()}
                >
                    <i className="bi bi-arrow-left"></i> Back
                </button>
                <h5 className="fw-bold mb-0">Political Website</h5>
                <div className="d-flex align-items-center gap-2">
                    <SetupGuideModal
                        storageKey={`dn_guide_political_${personaluid || "me"}`}
                        title="Set up your Political Website"
                        intro="Build your public political profile. Fill these sections to launch your website."
                        steps={[
                            {
                                title: "Profile",
                                desc: "Personal details, position, party, tagline, contact info, biography, profile photo and cover image.",
                            },
                            {
                                title: "Achievements & Timeline",
                                desc: "Your key achievements and career timeline — displayed on your live site.",
                            },
                            {
                                title: "Gallery & Settings",
                                desc: "Photo gallery, social media links, YouTube video, and website theme colour.",
                            },
                        ]}
                    />
                    {domain && (
                        <a
                            className="btn btn-sm btn-outline-success"
                            target="_blank"
                            rel="noreferrer"
                            href={`/${domain}`}
                        >
                            <i className="bi bi-box-arrow-up-right me-1" /> View Live Website
                        </a>
                    )}
                </div>
            </div>

            {/* Tab Navigation */}
            <ul className="nav nav-tabs mb-3">
                {tabs.map(([k, label]) => (
                    <li className="nav-item" key={k}>
                        <button
                            className={`nav-link ${tab === k ? "active" : ""}`}
                            onClick={() => setTab(k)}
                            type="button"
                        >
                            {label}
                        </button>
                    </li>
                ))}
            </ul>

            {msg && <div className={`alert alert-${msg.type} py-2`}>{msg.text}</div>}

            {/* Tab Content */}
            {tab === "profile" && (
                <PoliticalProfileTab form={form} set={set} personaluid={personaluid} />
            )}
            {tab === "achievements" && (
                <PoliticalAchievementsTab
                    achievements={achievements}
                    setAchievements={setAchievements}
                    timeline={timeline}
                    setTimeline={setTimeline}
                />
            )}
            {tab === "gallery" && (
                <PoliticalGalleryTab
                    form={form}
                    set={set}
                    gallery={gallery}
                    setGallery={setGallery}
                    personaluid={personaluid}
                />
            )}

            {/* Save Button */}
            <div className="d-flex justify-content-end gap-2 mb-4 mt-4">
                <button
                    className="btn btn-primary px-4"
                    onClick={handleSave}
                    disabled={saving}
                >
                    {saving ? "Saving..." : "Save Website"}
                </button>
            </div>
        </div>
    );
}